import type { RawMatch } from "./bullionRawMatch";
import { primeInitialStateSnapshotsFromApiBody as cacheBullionInitialStateForPlayedView } from "../../../utils/streamInitialStatePrimedCache";
import { fetchStreamInitialStateBatched } from "../../../utils/cricketStreamSubscription";
import {
    IN_PLAY_STATUS_IDS,
    commentIndicatesCricketMatchResult,
    eventStatusStringKey,
    eventStatusToNumber,
    isMatchFinishedStatus,
    isMatchScheduledStatus,
} from "../../Trader/Cricket/decimalEventStatus";

function extractStreamEventStatusFromInitialSnap(snap: unknown): unknown {
    if (!snap || typeof snap !== "object") return null;
    const o = snap as Record<string, unknown>;
    const pick = (v: unknown) => (v != null && v !== "" ? v : null);

    const sc = o.scorecard;
    if (sc && typeof sc === "object") {
        const es = pick((sc as Record<string, unknown>).event_status);
        if (es != null) return es;
    }
    const mu = o.market_update;
    if (mu && typeof mu === "object") {
        const m = mu as Record<string, unknown>;
        const esTop = pick(m.event_status);
        if (esTop != null) return esTop;
        const inner = m.market_update;
        if (inner && typeof inner === "object") {
            const esInner = pick((inner as Record<string, unknown>).event_status);
            if (esInner != null) return esInner;
        }
    }
    const ev = o.event;
    if (ev && typeof ev === "object") {
        const e = ev as Record<string, unknown>;
        const esEv = pick(e.event_status);
        if (esEv != null) return esEv;
        const md = e.msg_data;
        if (md && typeof md === "object") {
            const esMd = pick((md as Record<string, unknown>).event_status);
            if (esMd != null) return esMd;
        }
    }
    return null;
}

function extractStreamStatusTextFromInitialSnap(snap: unknown): string {
    if (!snap || typeof snap !== "object") return "";
    const o = snap as Record<string, unknown>;
    const sc = o.scorecard;
    if (sc && typeof sc === "object") {
        const st = (sc as Record<string, unknown>).status;
        if (st != null && String(st).trim()) return String(st).trim();
    }
    const ev = o.event;
    if (ev && typeof ev === "object") {
        const md = (ev as Record<string, unknown>).msg_data;
        if (md && typeof md === "object") {
            const st = (md as Record<string, unknown>).status;
            if (st != null && String(st).trim()) return String(st).trim();
        }
    }
    return "";
}

function bullionLiveScorePatchFromStreamSnap(snap: unknown): RawMatch["liveScore"] | null {
    const es = extractStreamEventStatusFromInitialSnap(snap);
    if (es != null && isMatchFinishedStatus(es)) return { isLive: false, isFinished: true };

    const statusText = extractStreamStatusTextFromInitialSnap(snap);
    if (statusText && isMatchFinishedStatus(statusText)) return { isLive: false, isFinished: true };
    const u = statusText.toUpperCase();
    if (
        u.includes("MATCH COMPLETE") ||
        u.includes("EVENT CLOSED") ||
        u.includes("MATCH ABANDONED")
    ) {
        return { isLive: false, isFinished: true };
    }

    const o = snap as Record<string, unknown>;
    const gp = String(o?.game_phase ?? "").trim();
    if (/\bevent\s*closed\b/i.test(gp) || /\bmatch\s*complete\b/i.test(gp)) {
        return { isLive: false, isFinished: true };
    }
    const comment =
        (o?.commentary && typeof o.commentary === "object"
            ? (o.commentary as Record<string, unknown>).text
            : null) ?? o?.match_comment;
    if (commentIndicatesCricketMatchResult(comment)) return { isLive: false, isFinished: true };

    if (es == null) return null;
    if (isMatchScheduledStatus(es)) return null;
    const n = eventStatusToNumber(es);
    if (n != null && (IN_PLAY_STATUS_IDS as readonly number[]).includes(n)) return { isLive: true, isFinished: false };
    if (typeof es === "string" && es.trim()) {
        const key = eventStatusStringKey(es);
        if (key && key !== "MATCH_SCHEDULED" && !isMatchFinishedStatus(es)) return { isLive: true, isFinished: false };
    }
    return null;
}

/**
 * Cricket list rows from trade match endpoints often omit live status (odds live on the events server).
 * Merge a slim lobby `initial-state` snapshot so BULLION can mark live/finished boards.
 */
export async function enrichMatchesWithCricketStreamInitialState(matches: RawMatch[]): Promise<RawMatch[]> {
    const baseApi = process.env.STREAM_API_URL;
    const streamBase = (baseApi || "").replace(/\/stream.*$/i, "");
    if (!streamBase || matches.length === 0) return matches;

    const idsNeedingMarkets = new Set<string>();
    for (const m of matches) {
        if (String(m.sport || "").toLowerCase() !== "cricket") continue;
        const id = String(m.id ?? "").trim();
        if (id) idsNeedingMarkets.add(id);
    }
    const sortedIds = [...idsNeedingMarkets].sort();
    if (sortedIds.length === 0) return matches;

    const INITIAL_STATE_FETCH_MS = 6000;
    let state: Record<string, unknown> = {};
    try {
        const controller = new AbortController();
        const timer =
            typeof window !== "undefined"
                ? window.setTimeout(() => controller.abort(), INITIAL_STATE_FETCH_MS)
                : undefined;
        try {
            state = await fetchStreamInitialStateBatched(
                streamBase,
                sortedIds,
                "&tier=lobby",
                controller.signal
            );
        } finally {
            if (timer != null) window.clearTimeout(timer);
        }
    } catch {
        return matches;
    }

    cacheBullionInitialStateForPlayedView(state);

    const liveScoreByMatchId = new Map<string, NonNullable<RawMatch["liveScore"]>>();
    for (const [matchId, snap] of Object.entries(state)) {
        const ls = bullionLiveScorePatchFromStreamSnap(snap);
        if (ls) liveScoreByMatchId.set(String(matchId), ls);
    }
    if (liveScoreByMatchId.size === 0) return matches;

    return matches.map((m) => {
        if (String(m.sport || "").toLowerCase() !== "cricket") return m;
        const mid = String(m.id ?? "");
        let next: RawMatch = m;
        const ls = liveScoreByMatchId.get(mid);
        if (ls) {
            const prev = next.liveScore && typeof next.liveScore === "object" ? next.liveScore : {};
            next = { ...next, liveScore: { ...prev, ...ls } };
        }
        // BULLION board uses frozen opening_odds only — do not merge live stream markets (stable line ids for settlement).
        return next;
    });
}
